import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../api";
import { Alerts, Badge, EmptyRow, Field, Loading, Page, Pager, Tabs, UserLink } from "../components/ui";
import { label, money, PAGE_SIZE, qs, setQuery, text, when } from "../format";

function shortValue(value) {
  const full = String(value || "");
  if (!full) return "—";
  if (full.length <= 18) return full;
  return full.slice(0, 8) + "…" + full.slice(-6);
}

export default function Bep20Deposits() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const transactionType = params.get("transactionType") || "";
  const userId = params.get("userId") || "";
  const [draft, setDraft] = useState(userId);
  const list = useFetch("/bep20-deposits" + qs({ page, limit: PAGE_SIZE, transactionType, userId }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];

  useEffect(() => setDraft(userId), [userId]);

  return (
    <Page title="BEP20 deposits" text="On-chain rows. This list is separate from screenshot deposits. DEPOSIT credits the member's single wallet. MISCELLANEOUS is below 0.1 or above the maximum, so the wallet was not credited. Members cannot see these rows. Transfer is the hourly sweep from the member deposit address to the admin hot wallet, or the gas for that sweep. It does not change the site wallet balance.">
      <Alerts error={list.error} />
      <form className="toolbar" onSubmit={(event) => {
        event.preventDefault();
        setQuery(params, setParams, { userId: draft.trim(), page: "" });
      }}>
        <Field label="Member id" error={list.errors.userId}>
          <input value={draft} onChange={(event) => setDraft(event.target.value)} />
        </Field>
        <button className="btn small">Filter</button>
        {userId ? (
          <button type="button" className="btn ghost small" onClick={() => setQuery(params, setParams, { userId: "", page: "" })}>Clear</button>
        ) : null}
      </form>
      <Tabs
        value={transactionType}
        onChange={(value) => setQuery(params, setParams, { transactionType: value, page: "" })}
        options={[
          { value: "", label: "All" },
          { value: "DEPOSIT", label: "Deposit" },
          { value: "MISCELLANEOUS", label: "Miscellaneous" },
          { value: "Transfer", label: "Transfer" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Member</th>
                <th>Coin</th>
                <th>Chain</th>
                <th className="num">Amount</th>
                <th>Type</th>
                <th>Hash</th>
                <th>From</th>
                <th>To</th>
                <th>Status</th>
                <th>Description</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id}>
                  <td><UserLink user={row.user} userId={row.userId} /></td>
                  <td className="code">{text(row.shortName)}</td>
                  <td>{label(row.chain)}</td>
                  <td className="num">{money(row.amount)}</td>
                  <td><Badge value={String(row.transactionType || "").toLowerCase()} /></td>
                  <td className="mono" title={row.transactionHash || ""}>{shortValue(row.transactionHash)}</td>
                  <td className="mono" title={row.fromAddress || ""}>{shortValue(row.fromAddress)}</td>
                  <td className="mono" title={row.toAddress || ""}>{shortValue(row.toAddress)}</td>
                  <td><Badge value={String(row.status || "").toLowerCase()} /></td>
                  <td>{text(row.description)}</td>
                  <td>{when(row.createdAt)}</td>
                </tr>
              )) : <EmptyRow cols={11} text={list.error ? "Could not load deposits" : "No BEP20 deposits"} />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
    </Page>
  );
}
